import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { toast } from "react-toastify"

import {
  IntegrationDocument,
  useUpdateFusionIntegrationMutation
} from "src/graphql/quark/quark.type"

type SettingSpec = {
  key: string
  label: string
  helpText?: string | null
  secret: boolean
  required: boolean
}

type Integration = {
  id: string
  displayName: string
  environment: string
  callbackKey?: string | null
  settingSpecs: SettingSpec[]
  settings: { key: string; value: string }[]
}

const ConfigureTab = ({
  integration,
  canEdit
}: {
  integration: Integration
  canEdit: boolean
}) => {
  const [displayName, setDisplayName] = useState(integration.displayName)
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(integration.settings.map((s) => [s.key, s.value]))
  )
  const [secrets, setSecrets] = useState<Record<string, string>>({})

  const [update, { loading }] = useUpdateFusionIntegrationMutation({
    refetchQueries: [
      {
        query: IntegrationDocument,
        variables: { connectionId: integration.id }
      }
    ]
  })

  const handleSave = async () => {
    const changedSecrets = Object.entries(secrets).filter(([, v]) => v.trim())

    try {
      await update({
        variables: {
          input: {
            connectionId: integration.id,
            displayName,
            settings: Object.entries(values).map(([key, value]) => ({
              key,
              value
            })),
            // Omitted entirely when untouched, so blank fields never wipe
            // stored secrets.
            credentials:
              changedSecrets.length > 0
                ? changedSecrets.map(([key, value]) => ({
                    key,
                    value: value.trim()
                  }))
                : undefined
          }
        }
      })
      setSecrets({})
      toast.success("Settings saved")
    } catch (error: any) {
      toast.error(error?.message ?? "Could not save settings")
    }
  }

  const webhookUrl = integration.callbackKey
    ? `${window.location.origin.replace(/^https?:\/\/[^.]*/, "https://api")}/v1/webhooks/${integration.callbackKey}`
    : null

  return (
    <Box maxWidth={640}>
      <TextField
        fullWidth
        size="small"
        label="Name"
        sx={{ mb: 2 }}
        disabled={!canEdit}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
      />

      {integration.settingSpecs
        .filter((spec) => !spec.secret)
        .map((spec) => (
          <TextField
            key={spec.key}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            label={spec.label}
            helperText={spec.helpText ?? undefined}
            required={spec.required}
            disabled={!canEdit}
            value={values[spec.key] ?? ""}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [spec.key]: e.target.value }))
            }
          />
        ))}

      <Typography variant="subtitle2" mt={3} mb={1}>
        Credentials
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Saved credentials are never shown again. Leave a field blank to keep the
        current value.
      </Alert>

      {integration.settingSpecs
        .filter((spec) => spec.secret)
        .map((spec) => (
          <TextField
            key={spec.key}
            fullWidth
            size="small"
            type="password"
            sx={{ mb: 2 }}
            label={spec.label}
            helperText={spec.helpText ?? undefined}
            placeholder="Unchanged"
            disabled={!canEdit}
            value={secrets[spec.key] ?? ""}
            onChange={(e) =>
              setSecrets((prev) => ({ ...prev, [spec.key]: e.target.value }))
            }
          />
        ))}

      {webhookUrl && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Webhook URL
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Paste this into the provider&apos;s portal so they can send you
              orders and job results.
            </Typography>
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              value={webhookUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => {
                          void navigator.clipboard.writeText(webhookUrl)
                          toast.success("Webhook URL copied")
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </CardContent>
        </Card>
      )}

      <Box mt={3}>
        <Button
          variant="contained"
          disabled={!canEdit || loading}
          onClick={() => void handleSave()}
        >
          Save changes
        </Button>
      </Box>
    </Box>
  )
}

export default ConfigureTab
